<script setup lang="ts">
/*
 * Small wrapper over NuxtImg
 * Supports fallback to default img tag when NuxtImg was unable to optimize the image.
 * This could happen if an image file is corrupted (e.g. wrongly saved by an image editing software)
 * However, providing a fallback is always a good practise
 */

interface Emits {
  (e: 'error', data: any): void;
  (e: 'load', data: any): void;
}

interface Props {
  src?: string;
  fallback?: string;
  alt?: string;
}

const emit = defineEmits<Emits>();

const props = defineProps<Props>();

const attrs = useAttrs();

const propsWithoutFallbackAndSrc = computed(() => {
  const { fallback, src, ...restProps } = props;

  return { ...attrs, ...restProps };
});

const slots = useSlots();

const hasFallbackSlot = computed<boolean>(() => {
  return !!slots.fallback;
});

const isPrimaryImageVisible = computed<boolean>(() => {
  return !hasError.value || !(props.fallback || hasFallbackSlot.value);
});

const hasError = ref(false);

const handleError = (data: any) => {
  emit('error', data);
  hasError.value = true;
};
</script>

<template>
  <NuxtImg
    v-if="isPrimaryImageVisible"
    v-bind="propsWithoutFallbackAndSrc"
    :alt="props.alt"
    loading="lazy"
    :src="props.src"
    @error="handleError"
    @load="emit('load', $event)"
  />
  <template v-else>
    <slot name="fallback">
      <img
        v-bind="propsWithoutFallbackAndSrc"
        :alt="props.alt"
        :src="props.fallback"
      />
    </slot>
  </template>
</template>
